<i18n locale="en" lang="json">
{
  "date": "Date",
  "invalidDate": "This is not a valid date. Date format is {format}."
}
</i18n>
<i18n locale="nl" lang="json">
{
  "date": "Datum",
  "invalidDate": "Dit is geen geldige datum. Datum formaat is {format}."
}
</i18n>

<template>
  <text-field
    v-model="userInputValue"
    v-bind="{ ...$attrs, label: ($attrs.label as string) || t('date') }"
    :validation="mergedValidationModels"
    :disabled="$attrs.disabled"
    :placeholder="dateFormat"
    :test-id="[componentName, testId || getModelExpression()]"
    @focus="onFocus"
    @click="onClick"
    @blur="onBlur"
  >
    <template #prepend-inner>
      <input
        ref="nativeDateField"
        v-model="internalValue"
        type="date"
        style="position: absolute; opacity: 1; width: 1px; height: 1px"
        tabindex="-1"
        class="mt-10 ml-md-2"
        :min="min"
        :max="max"
        @update:model-value="onDatePickerChange"
      />
    </template>
    <template v-if="!$attrs.disabled" #append-inner>
      <button
        v-if="$attrs.clearable && !!userInputValue"
        v-set-test-id="[componentName, testId || getModelExpression(), 'clear']"
        class="mr-2"
        @click.prevent="userInputValue = undefined"
      >
        <font-awesome-icon :icon="faTimesCircle" />
      </button>
      <button
        v-set-test-id="[
          componentName,
          testId || getModelExpression(),
          'open-menu',
        ]"
        class="mr-2"
        @click.prevent="showPicker"
      >
        <font-awesome-icon :icon="faCalendarAlt" />
      </button>
    </template>
  </text-field>
</template>

<script lang="ts" setup>
import {
  faCalendarAlt,
  faTimesCircle,
} from '@fortawesome/pro-regular-svg-icons';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { helpers } from '@vuelidate/validators';
import type { BaseValidation } from '@vuelidate/core';
import useVuelidate from '@vuelidate/core';
import customDebounce from '@/common/debounce';
import useLuxon from '@/common/composition/useLuxon';
import useIsMobile from '@/common/composition/useIsMobile';
import type { FormFieldEmits } from '@/common/components/form/FormField.vue';
import { useValue } from '@/common/components/form/FormField.vue';
import zeroPrefill from '@/common/zeroPrefill';
import type { ActionableComponentProps } from '@/common/components/ActionableComponent';
import {
  vSetTestId,
  getModelExpression,
} from '@/common/components/ActionableComponent';

const componentName = 'DateField';

type Props = ActionableComponentProps & {
  modelValue?: string;
  debounce?: number;
  validation?: BaseValidation;
  min?: string;
  max?: string;
};
const props = defineProps<Props>();
const emit = defineEmits<FormFieldEmits<string>>();

const { t, locale } = useI18n();
const { d } = useI18n({ useScope: 'global' });
const { parseToDate } = useLuxon();
const isMobile = useIsMobile();
let isFocussed = false;

const nativeDateField = ref<
  HTMLInputElement & { showPicker: () => undefined }
>();
const internalValue = useValue<string>(props, emit);

const regexValidMonthFirst =
  /^(0?[1-9]|1[012])\D+(0?[1-9]|[12][0-9]|3[01])\D+((19|20)\d\d)$/;
const regexValidDayFirst =
  /^(0?[1-9]|[12][0-9]|3[01])\D+(0?[1-9]|1[012])\D+((19|20)\d\d)$/;
const localeDateOutput = computed(() => d(new Date('2022-07-05'), 'short'));
const dateFormat = computed(() =>
  localeDateOutput.value
    .replace('2022', 'YYYYY')
    .replace('07', 'MM')
    .replace('7', 'M')
    .replace('05', 'DD')
    .replace('5', 'D'),
);
const startsWithDay = computed(() => dateFormat.value.charAt(0) === 'D');

const isoDateToFormattedDate = (date: string | undefined) =>
  date ? d(parseToDate(date), 'short') : undefined;

const userInputValue = ref<string | undefined>(
  isoDateToFormattedDate(props.modelValue),
);

const validDate = computed(() => {
  if (!userInputValue.value) {
    return undefined;
  }
  const match = userInputValue.value.match(
    startsWithDay.value ? regexValidDayFirst : regexValidMonthFirst,
  );
  if (!match) {
    return undefined;
  }
  const inputDay = Number(match[startsWithDay.value ? 1 : 2]);
  const inputMonth = Number(match[startsWithDay.value ? 2 : 1]);
  const day = zeroPrefill(inputDay, 2);
  const month = zeroPrefill(inputMonth, 2);
  return `${match[3]}-${month}-${day}`;
});

const validationRules = {
  userInputValue: {
    timeIncorrect: helpers.withMessage(
      t('invalidDate', { format: dateFormat.value }),
      () => !userInputValue.value || Boolean(internalValue.value),
    ),
  },
};
const v$ = useVuelidate(validationRules, { userInputValue }, { $scope: false });
const mergedValidationModels = computed(() => {
  if (!props.validation) {
    return v$.value;
  }
  return {
    ...props.validation,
    $touch: (): void => {
      props.validation?.$touch();
      v$.value.$touch();
    },
    $errors: [...v$.value.$errors, ...props.validation.$errors],
  };
});

watch(internalValue, newValue => {
  if (!isFocussed) {
    userInputValue.value = isoDateToFormattedDate(newValue);
  }
});

watch(locale, () => {
  userInputValue.value = isoDateToFormattedDate(internalValue.value);
});

watch(validDate, newTime => {
  if (isFocussed) {
    internalValue.value = newTime;
  }
});

const showPicker = customDebounce(() => {
  if (!nativeDateField.value) {
    throw new Error('nativeDateField not found');
  }
  try {
    nativeDateField.value.showPicker();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log(e);
  }
}, 150);

const onClick = () => {
  if (isMobile) {
    showPicker();
  }
};

const onFocus = (event: FocusEvent) => {
  isFocussed = true;
  if (isMobile) {
    showPicker();
    return;
  }
  if (event.currentTarget) {
    (event.currentTarget as HTMLInputElement).select();
  }
};

const onBlur = () => {
  isFocussed = false;
  if (validDate.value) {
    userInputValue.value = isoDateToFormattedDate(validDate.value);
  }
};

const onDatePickerChange = () => {
  userInputValue.value = isoDateToFormattedDate(internalValue.value);
};
</script>
